import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from 'react';
import PostService from '../services/postService';
import { useAuth } from '../hooks/useAuth';

// Types
export interface Post {
  id: string;
  type: string;
  title: string;
  content: string;
  author: {
    id: string;
    username: string;
    profileImage: string | null;
    bio?: string | undefined;
    followersCount?: number | undefined;
    followingCount?: number | undefined;
    isVerified?: boolean | undefined;
    joinedDate?: string | undefined;
    website?: string | undefined;
  };
  timestamp: string;
  reactions: {
    likes: number;
    helpful: number;
    comments: number;
    [key: string]: number;
  };
  shares: number;
  tags: Array<{
    id: string;
    name: string;
    description?: string;
    category?: string;
    severity?: string;
    icon?: string;
    color?: string;
  }>;
  subject?:
    | {
        name: string;
        location: string;
        verified: boolean;
        trustScore: number;
      }
    | undefined;
  isWarning?: boolean | undefined;
  userReaction?: string | undefined;
  userReactions?: string[] | undefined; // Track all user reactions, not just the highest priority
  isSaved?: boolean | undefined; // Track if user has saved/bookmarked this post
  isHidden?: boolean | undefined; // Track if post is hidden by user (blocked/not interested)
  attachments?: Array<{
    id: string;
    type: string;
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    url: string;
    thumbnailUrl?: string;
    duration?: number;
    width?: number;
    height?: number;
  }>;
}

interface PostsContextType {
  posts: Post[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMorePosts: boolean;
  currentPage: number;
  lastPostId: string | null;
  isLoadingNewPosts: boolean;
  updatePostReaction: (postId: string, reactionType: string) => Promise<void>;
  loadPosts: (
    page?: number,
    append?: boolean,
    cacheBust?: boolean
  ) => Promise<void>;
  loadMorePosts: () => Promise<void>;
  refreshPosts: (cacheBust?: boolean) => Promise<void>;
  checkForNewPosts: () => Promise<void>;
  showNewPosts: () => Promise<void>;
  addPostOptimistically: (post: Post) => void;
  createPost: (postData: {
    title: string;
    content: string;
    tags: Array<{
      id: string;
      name: string;
      description?: string;
      category?: string;
      severity?: string;
      icon?: string;
      color?: string;
    }>;
  }) => Promise<void>;
  submitComment: (postId: string, content: string) => Promise<void>;
  toggleComments: (postId: string) => void;
  expandedComments: Record<string, boolean>;
  setExpandedComments: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  getComments: (postId: string, page?: number, limit?: number) => Promise<any>;
  updatePost: (postId: string, updates: Partial<Post>) => void;
}

const PostsContext = createContext<PostsContextType | undefined>(undefined);

// Transform API post to frontend Post type
export const transformApiPost = (apiPost: Record<string, unknown>): Post => ({
  id: String(apiPost.id),
  type: String(apiPost.subjectType || 'general').toLowerCase(),
  title: String(apiPost.title || ''),
  content: String(apiPost.content),
  author: {
    id: String(
      (apiPost.author as Record<string, unknown>)?.id ||
        apiPost.authorId ||
        'unknown'
    ),
    username: String(
      (apiPost.author as Record<string, unknown>)?.username || 'user'
    ),
    profileImage:
      ((apiPost.author as Record<string, unknown>)?.profileImage as
        | string
        | null) || null,
    bio: (apiPost.author as Record<string, unknown>)?.bio as string | undefined,
    followersCount: (apiPost.author as Record<string, unknown>)
      ?.followersCount as number | undefined,
    followingCount: (apiPost.author as Record<string, unknown>)
      ?.followingCount as number | undefined,
    isVerified: (apiPost.author as Record<string, unknown>)?.isVerified as
      | boolean
      | undefined,
    joinedDate: (apiPost.author as Record<string, unknown>)?.joinedDate as
      | string
      | undefined,
    website: (apiPost.author as Record<string, unknown>)?.website as
      | string
      | undefined,
  },
  timestamp: String(apiPost.createdAt || new Date().toISOString()),
  reactions: {
    likes: Number((apiPost.stats as Record<string, unknown>)?.likes || 0),
    helpful: Number((apiPost.stats as Record<string, unknown>)?.helpful || 0),
    comments: Number((apiPost.stats as Record<string, unknown>)?.comments || 0),
  },
  shares: Number((apiPost.stats as Record<string, unknown>)?.shares || 0),
  tags:
    (apiPost.tags as Array<{
      id: string;
      name: string;
      description?: string;
      category?: string;
      severity?: string;
      icon?: string;
      color?: string;
    }>) || [],
  subject: apiPost.subject
    ? {
        name: String((apiPost.subject as Record<string, unknown>).name),
        location: String((apiPost.subject as Record<string, unknown>).location),
        verified: Boolean(
          (apiPost.subject as Record<string, unknown>).verified
        ),
        trustScore: Number(
          (apiPost.subject as Record<string, unknown>).trustScore
        ),
      }
    : undefined,
  isWarning: Boolean(apiPost.isWarning || false),
  userReaction:
    (apiPost.userReaction as string | null | undefined) ?? undefined,
  userReactions:
    (apiPost.userReactions as string[] | undefined) ||
    (apiPost.userReaction ? [apiPost.userReaction as string] : []), // Initialize with userReaction if available
  attachments:
    (apiPost.attachments as Array<{
      id: string;
      type: string;
      filename: string;
      originalName: string;
      mimeType: string;
      size: number;
      url: string;
      thumbnailUrl?: string;
      duration?: number;
      width?: number;
      height?: number;
    }>) || [],
});

export const PostsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  // Track pending reactions to prevent race conditions
  const pendingReactions = useRef<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Load lastPostId from localStorage on mount
  const [lastPostId, setLastPostIdState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastPostId');
    }
    return null;
  });

  // Persist lastPostId to localStorage whenever it changes
  const setLastPostId = useCallback((postId: string | null) => {
    setLastPostIdState(postId);
    if (typeof window !== 'undefined') {
      if (postId) {
        localStorage.setItem('lastPostId', postId);
      } else {
        localStorage.removeItem('lastPostId');
      }
    }
  }, []);

  const [isLoadingNewPosts, setIsLoadingNewPosts] = useState(false);
  const [expandedComments, setExpandedComments] = useState<
    Record<string, boolean>
  >({});

  // Prevent duplicate simultaneous loads
  const isLoadingRef = useRef(false);

  // Load posts from API
  const loadPosts = useCallback(
    async (page = 1, append = false, cacheBust = false) => {
      // Prevent multiple simultaneous calls
      if (isLoadingRef.current && page === 1 && !append) {
        return;
      }

      if (page === 1) {
        isLoadingRef.current = true;
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        // Load feed - always fresh on page 1 to ensure user sees their own posts
        const response = await PostService.getPostsFeed({
          page,
          limit: 20,
          cacheBust: cacheBust || page === 1, // Always bypass cache on page 1 to get latest posts
        });

        const apiPosts = response.posts || [];
        const transformedPosts: Post[] = apiPosts.map(transformApiPost);

        if (append) {
          setPosts(prevPosts => [...prevPosts, ...transformedPosts]);
        } else {
          setPosts(transformedPosts);
          // Set the ID of the first post for new posts detection
          if (transformedPosts.length > 0) {
            setLastPostId(transformedPosts[0].id);
          }
        }

        // Handle both response.hasMore and response.pagination.hasMore for compatibility
        setHasMorePosts(
          response.hasMore !== undefined
            ? response.hasMore
            : response.pagination?.hasMore || false
        );
        setCurrentPage(page);
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    []
  );

  // Load more posts
  const loadMorePosts = useCallback(async () => {
    if (!isLoadingMore && hasMorePosts) {
      await loadPosts(currentPage + 1, true);
    }
  }, [isLoadingMore, hasMorePosts, currentPage, loadPosts]);

  // Refresh posts
  const refreshPosts = useCallback(
    async (cacheBust = false) => {
      if (cacheBust) {
        // Bypass cache when refreshing after creating a post
        setIsLoading(true);
        try {
          const response = await PostService.getPostsFeed({
            page: 1,
            limit: 20,
            cacheBust: true,
          });

          const apiPosts = response.posts || [];
          const transformedPosts: Post[] = apiPosts.map(transformApiPost);

          // Merge with existing posts to preserve any optimistically added posts
          // Remove duplicates and ensure newest posts are at the top
          setPosts(prevPosts => {
            // Create a map of existing posts by ID
            const existingPostMap = new Map(prevPosts.map(p => [p.id, p]));

            // Add/update posts from server response
            transformedPosts.forEach(post => {
              existingPostMap.set(post.id, post);
            });

            // Convert back to array and sort by timestamp (newest first)
            const mergedPosts = Array.from(existingPostMap.values()).sort(
              (a, b) => {
                const timeA = new Date(a.timestamp).getTime();
                const timeB = new Date(b.timestamp).getTime();
                return timeB - timeA; // Descending order (newest first)
              }
            );

            return mergedPosts;
          });

          // Set the ID of the first post for new posts detection
          if (transformedPosts.length > 0) {
            setLastPostId(transformedPosts[0].id);
          }

          // Handle both response.hasMore and response.pagination.hasMore for compatibility
          setHasMorePosts(
            response.hasMore !== undefined
              ? response.hasMore
              : response.pagination?.hasMore || false
          );
          setCurrentPage(1);
        } catch (error) {
          console.error('Error loading posts:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        await loadPosts(1, false);
      }
    },
    [loadPosts]
  );

  // Check for new posts
  const checkForNewPosts = useCallback(async () => {
    if (!lastPostId || isLoadingNewPosts) return;

    setIsLoadingNewPosts(true);
    try {
      const response = await PostService.checkForNewPosts(lastPostId);
      // This will be handled by the component that uses this context
      void response; // Suppress unused variable warning
    } catch (error) {
      console.error('Error checking for new posts:', error);
    } finally {
      setIsLoadingNewPosts(false);
    }
  }, [lastPostId, isLoadingNewPosts]);

  // Show new posts - fetch new posts and prepend to feed, avoiding duplicates
  const showNewPosts = useCallback(async () => {
    if (!lastPostId || isLoadingNewPosts) return;

    setIsLoadingNewPosts(true);
    try {
      // Fetch new posts since lastPostId
      const response = await PostService.checkForNewPosts(lastPostId);
      const newApiPosts = response.posts || [];

      if (newApiPosts.length === 0) {
        // No new posts, just refresh to be sure
        await refreshPosts();
        return;
      }

      // Transform new posts
      const transformedNewPosts: Post[] = newApiPosts.map(transformApiPost);

      // Get existing post IDs to avoid duplicates
      setPosts(prevPosts => {
        const existingPostIds = new Set(prevPosts.map(p => p.id));

        // Filter out posts that already exist in the feed
        const uniqueNewPosts = transformedNewPosts.filter(
          newPost => !existingPostIds.has(newPost.id)
        );

        // If no unique posts, just return previous state
        if (uniqueNewPosts.length === 0) {
          return prevPosts;
        }

        // Prepend new posts to the top of the feed
        const updatedPosts = [...uniqueNewPosts, ...prevPosts];

        // Update lastPostId to the newest post (first in the array)
        if (uniqueNewPosts.length > 0) {
          setLastPostId(uniqueNewPosts[0].id);
        }

        return updatedPosts;
      });
    } catch (error) {
      console.error('Error showing new posts:', error);
      // Fallback to full refresh on error
      await refreshPosts();
    } finally {
      setIsLoadingNewPosts(false);
    }
  }, [lastPostId, isLoadingNewPosts, refreshPosts]);

  // Update post reaction
  const updatePostReaction = useCallback(
    async (postId: string, reactionType: string) => {
      if (!user) {
        console.error('User not authenticated');
        alert('Please log in to react to posts');
        return;
      }

      // Prevent multiple simultaneous requests for the same post
      const requestKey = `${postId}-${reactionType}`;
      if (pendingReactions.current.has(requestKey)) {
        return;
      }

      // Map frontend reaction types to API types (defined outside try block for catch block access)
      const apiReactionType =
        reactionType === 'likes'
          ? 'LIKE'
          : reactionType === 'helpful'
            ? 'HELPFUL'
            : reactionType.toUpperCase();

      try {
        // Add to pending set
        pendingReactions.current.add(requestKey);

        // Optimistic update
        setPosts(prevPosts =>
          prevPosts.map(post => {
            if (post.id === postId) {
              const currentPost = post;
              // Check if user already has this specific reaction type
              const currentUserReactions =
                currentPost.userReactions ||
                (currentPost.userReaction ? [currentPost.userReaction] : []);
              const hasThisReaction =
                currentUserReactions.includes(apiReactionType);

              // Update user reactions array
              const newUserReactions = hasThisReaction
                ? currentUserReactions.filter(r => r !== apiReactionType)
                : [...currentUserReactions, apiReactionType];

              // Determine the highest priority reaction for userReaction display
              const reactionPriority: Record<string, number> = {
                HELPFUL: 5,
                LOVE: 4,
                SAD: 3,
                ANGRY: 2,
                LIKE: 1,
              };
              const highestPriorityReaction =
                newUserReactions.length > 0
                  ? newUserReactions.reduce((prev, current) =>
                      (reactionPriority[current] || 0) >
                      (reactionPriority[prev] || 0)
                        ? current
                        : prev
                    )
                  : undefined;

              return {
                ...post,
                reactions: {
                  ...post.reactions,
                  [reactionType]: hasThisReaction
                    ? Math.max(0, (post.reactions[reactionType] || 0) - 1)
                    : (post.reactions[reactionType] || 0) + 1,
                },
                userReaction: highestPriorityReaction,
                userReactions: newUserReactions,
              };
            }
            return post;
          })
        );

        // Make API call
        const result = await PostService.reactToPost(postId, {
          type: apiReactionType as any,
        });

        // Update post with server response (contains accurate stats)
        if (result && result.stats) {
          setPosts(prevPosts =>
            prevPosts.map(post => {
              if (post.id === postId) {
                // Get current user reactions to determine if we need to update them
                const currentUserReactions =
                  post.userReactions ||
                  (post.userReaction ? [post.userReaction] : []);

                // Check if the reaction was added or removed based on the API response
                const hasReaction =
                  currentUserReactions.includes(apiReactionType);
                const reactionWasAdded = result.added !== false; // API returns { added: boolean }

                // Update user reactions array based on API response
                let updatedUserReactions: string[];
                if (reactionWasAdded && !hasReaction) {
                  // Reaction was added
                  updatedUserReactions = [
                    ...currentUserReactions,
                    apiReactionType,
                  ];
                } else if (!reactionWasAdded && hasReaction) {
                  // Reaction was removed
                  updatedUserReactions = currentUserReactions.filter(
                    r => r !== apiReactionType
                  );
                } else {
                  // No change needed
                  updatedUserReactions = currentUserReactions;
                }

                // Determine the highest priority reaction for userReaction display
                const reactionPriority: Record<string, number> = {
                  HELPFUL: 5,
                  LOVE: 4,
                  SAD: 3,
                  ANGRY: 2,
                  LIKE: 1,
                };
                const highestPriorityReaction =
                  updatedUserReactions.length > 0
                    ? updatedUserReactions.reduce((prev, current) =>
                        (reactionPriority[current] || 0) >
                        (reactionPriority[prev] || 0)
                          ? current
                          : prev
                      )
                    : undefined;

                return {
                  ...post,
                  reactions: {
                    ...post.reactions, // Keep existing reactions
                    likes: result.stats.likes || 0,
                    helpful: result.stats.helpful || 0,
                    // Note: love, sad, angry are not in Post interface but stats has them
                  },
                  userReaction:
                    highestPriorityReaction || result.stats.userReaction
                      ? String(
                          result.stats.userReaction || highestPriorityReaction
                        )
                      : undefined,
                  // Use userReactions from API if available, otherwise use our updated array
                  userReactions:
                    (result.stats.userReactions as string[] | undefined) ||
                    updatedUserReactions,
                };
              }
              return post;
            })
          );
        } else {
          // Fallback: refresh posts with cache busting if response format is unexpected
          await refreshPosts(true);
        }
      } catch (error) {
        console.error('Failed to react to post:', error);

        // Revert optimistic update on error
        setPosts(prevPosts =>
          prevPosts.map(post => {
            if (post.id === postId) {
              const currentPost = post;
              // Get the state before optimistic update (from the error)
              const currentUserReactions =
                currentPost.userReactions ||
                (currentPost.userReaction ? [currentPost.userReaction] : []);
              const hasThisReaction =
                currentUserReactions.includes(apiReactionType);

              // Revert: if we added it optimistically, remove it; if we removed it, add it back
              const revertedUserReactions = hasThisReaction
                ? currentUserReactions.filter(r => r !== apiReactionType)
                : [...currentUserReactions, apiReactionType];

              // Determine the highest priority reaction
              const reactionPriority: Record<string, number> = {
                HELPFUL: 5,
                LOVE: 4,
                SAD: 3,
                ANGRY: 2,
                LIKE: 1,
              };
              const highestPriorityReaction =
                revertedUserReactions.length > 0
                  ? revertedUserReactions.reduce((prev, current) =>
                      (reactionPriority[current] || 0) >
                      (reactionPriority[prev] || 0)
                        ? current
                        : prev
                    )
                  : undefined;

              return {
                ...post,
                reactions: {
                  ...post.reactions,
                  [reactionType]: hasThisReaction
                    ? (post.reactions[reactionType] || 0) + 1
                    : Math.max(0, (post.reactions[reactionType] || 0) - 1),
                },
                userReaction: highestPriorityReaction,
                userReactions: revertedUserReactions,
              };
            }
            return post;
          })
        );
      } finally {
        // Remove from pending set
        pendingReactions.current.delete(requestKey);
      }
    },
    [user]
  );

  // Create post
  const createPost = useCallback(
    async (postData: {
      title: string;
      content: string;
      tags: string[] | Array<{ id: string; name: string }>;
    }) => {
      try {
        await PostService.createPost(postData);
        // Refresh posts to show the new post
        await refreshPosts();
      } catch (error) {
        console.error('Failed to create post:', error);
        throw error;
      }
    },
    [refreshPosts]
  );

  // Submit comment
  const submitComment = useCallback(
    async (postId: string, content: string) => {
      try {
        // Optimistic update
        setPosts(prevPosts =>
          prevPosts.map(post =>
            post.id === postId
              ? {
                  ...post,
                  reactions: {
                    ...post.reactions,
                    comments: (post.reactions.comments || 0) + 1,
                  },
                }
              : post
          )
        );

        // Use the PostService to add comment
        await PostService.addComment(postId, { content });

        // Refresh posts with cache busting to get accurate counts
        await refreshPosts(true);
      } catch (error) {
        console.error('Failed to submit comment:', error);

        // Revert optimistic update on error
        setPosts(prevPosts =>
          prevPosts.map(post =>
            post.id === postId
              ? {
                  ...post,
                  reactions: {
                    ...post.reactions,
                    comments: Math.max(0, (post.reactions.comments || 0) - 1),
                  },
                }
              : post
          )
        );

        throw error;
      }
    },
    [refreshPosts]
  );

  // Add post optimistically to the top of the feed
  const addPostOptimistically = useCallback(
    (post: Post) => {
      setPosts(prevPosts => {
        // Check if post already exists to avoid duplicates
        if (prevPosts.some(p => p.id === post.id)) {
          return prevPosts;
        }
        return [post, ...prevPosts];
      });
      // Update lastPostId if this is the newest post
      if (post.id && (!lastPostId || new Date(post.timestamp) > new Date())) {
        setLastPostId(post.id);
      }
    },
    [lastPostId]
  );

  // Toggle comments
  const toggleComments = useCallback((postId: string) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  }, []);

  // Get comments for a post
  const getComments = useCallback(
    async (postId: string, page = 1, limit = 20) => {
      try {
        return await PostService.getComments(postId, page, limit);
      } catch (error) {
        console.error('Failed to fetch comments:', error);
        throw error;
      }
    },
    []
  );

  // Update a post
  const updatePost = useCallback((postId: string, updates: Partial<Post>) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, ...updates } : post
      )
    );
  }, []);

  const value: PostsContextType = {
    posts,
    isLoading,
    isLoadingMore,
    hasMorePosts,
    currentPage,
    lastPostId,
    isLoadingNewPosts,
    updatePostReaction,
    loadPosts,
    loadMorePosts,
    refreshPosts,
    checkForNewPosts,
    showNewPosts,
    addPostOptimistically,
    createPost,
    submitComment,
    toggleComments,
    expandedComments,
    setExpandedComments,
    getComments,
    updatePost,
  };

  return (
    <PostsContext.Provider value={value}>{children}</PostsContext.Provider>
  );
};

export const usePosts = (): PostsContextType => {
  const context = useContext(PostsContext);
  if (context === undefined) {
    throw new Error('usePosts must be used within a PostsProvider');
  }
  return context;
};
